import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { X } from "@phosphor-icons/react";
import { useAuth } from "../../hooks/useAuth";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { closeSignInPrompt } from "../../redux/features/authPrompt/authPromptSlice";
import { useEffect, useCallback } from "react";
import { markSignInSaveNudgeDismissedThisSession } from "../../utils/engagement/sign-in-save-nudge-session";
import { trackEvent } from "../../utils/analytics/analytics";
import { Msg, useFormatMessage } from '../../i18n-review/client/Msg';

const messages = defineMessages({
  title: {
    id: "auth.signInPrompt.title",
    defaultMessage: "Sign in to save jobs",
    description: "Title for sign-in prompt modal encouraging users to sign in to save jobs",
  },
  description: {
    id: "auth.signInPrompt.description",
    defaultMessage: "Activate your account to save jobs, sync progress, and pick up your search on any device.",
    description: "Description text in sign-in prompt modal explaining benefits of activating an account",
  },
  primaryAction: {
    id: "auth.signInPrompt.primaryAction",
    defaultMessage: "Activate account",
    description: "Primary button label in sign-in prompt modal to activate an account immediately",
  },
  secondaryAction: {
    id: "auth.signInPrompt.secondaryAction",
    defaultMessage: "Maybe later",
    description: "Secondary button label in sign-in prompt modal to dismiss the prompt",
  },
  closeLabel: {
    id: "auth.signInPrompt.closeLabel",
    defaultMessage: "Close",
    description: "Accessibility label for close button in sign-in prompt modal",
  },
});

export function SignInPromptModal() {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { isAuthenticated, signIn } = useAuth();
  const isOpen = useAppSelector((state) => state.authPrompt.isSignInPromptOpen);
  const dispatch = useAppDispatch();

  const handleCloseOnly = useCallback(() => {
    dispatch(closeSignInPrompt());
  }, [dispatch]);

  const handleDismissWithoutSignIn = useCallback(() => {
    markSignInSaveNudgeDismissedThisSession();
    trackEvent({
      name: 'auth_interaction',
      params: { action: 'sign_in_prompt_dismiss', surface: 'sign_in_prompt_modal', locale: intl.locale },
    });
    dispatch(closeSignInPrompt());
  }, [dispatch, intl.locale]);

  useEffect(() => {
    if (isAuthenticated && isOpen) {
      dispatch(closeSignInPrompt());
    }
  }, [dispatch, isAuthenticated, isOpen]);

  // Fires once per open — this only reaches a signed-out visitor (the effect above closes the
  // modal immediately if they're authenticated), so it's the "login-wall friction" moment.
  useEffect(() => {
    if (isOpen) {
      trackEvent({
        name: 'sign_in_prompt_shown',
        params: { surface: 'sign_in_prompt_modal', locale: intl.locale },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per open transition, not on every intl.locale change
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sign-in-prompt-title"
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 px-4 py-8"
    >
      <div className="relative w-full max-w-md rounded-3xl bg-white dark:bg-[#1F1509] border border-[#DDD5C8] dark:border-[#4A3520] p-6 shadow-2xl dark:shadow-none">
        <button
          type="button"
          onClick={handleDismissWithoutSignIn}
          aria-label={formatMessage(messages.closeLabel)}
          className="absolute right-4 top-4 rounded-full p-2 text-[#7A6E5F] transition hover:bg-[#F2EADF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B5E45] dark:text-[#B8A48C] dark:hover:bg-[#2A1D0F] dark:focus-visible:ring-[#98CFA8]"
        >
          <span className="sr-only">
            <Msg {...messages.closeLabel} />
          </span>
          <X className="h-5 w-5" />
        </button>
        <div className="flex flex-col gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#C8DDCE] text-[#3B5E45] dark:bg-[#1A2E22] dark:text-[#98CFA8]">
            <svg
              className="h-6 w-6"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.5 18C6.5 15.5147 8.51472 13.5 11 13.5H13C15.4853 13.5 17.5 15.5147 17.5 18"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 6L6 4M20 6L18 4M4 14L6 16M18 16L20 14"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="space-y-2">
            <h2 id="sign-in-prompt-title" className="text-2xl font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
              <Msg {...messages.title} />
            </h2>
            <p className="text-base text-[#7A6E5F] dark:text-[#B8A48C]">
              <Msg {...messages.description} />
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
            <button
              type="button"
              onClick={handleDismissWithoutSignIn}
              className="inline-flex h-11 items-center justify-center rounded-full border border-[#DDD5C8] px-4 text-sm font-medium text-[#3D3529] transition hover:border-[#3B5E45]/40 hover:bg-[#F2EADF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B5E45] dark:border-[#4A3520] dark:text-[#C4A882] dark:hover:bg-[#2A1D0F] dark:focus-visible:ring-[#98CFA8]"
            >
              <Msg {...messages.secondaryAction} />
            </button>
            <button
              type="button"
              onClick={() => {
                trackEvent({
                  name: 'auth_interaction',
                  params: { action: 'sign_in_click', surface: 'sign_in_prompt_modal', locale: intl.locale },
                });
                handleCloseOnly();
                signIn(undefined, { screenHint: 'signup' });
              }}
              className="inline-flex h-11 items-center justify-center rounded-full bg-[#3B5E45] px-5 text-sm font-semibold text-white shadow-lg shadow-[#3B5E45]/25 transition hover:bg-[#2F4A38] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#98CFA8] dark:shadow-none"
            >
              <Msg {...messages.primaryAction} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


